import axios from "axios";
import { appConfig } from "../config/Config";

export type BookSection = "literature" | "humanities_life" | "young";
export type BookAdminSearch = Partial<Pick<AdminBook, "sk" | "title" | "author" | "barcode">>;
export type BookLoanHistoryStatus = "ACTIVE" | "ALL";

export interface AdminBook {
  pk?: string;
  sk: string;
  title: string;
  author: string;
  barcode?: string;
  image?: string;
  description?: string;
  maincopy?: string;
  tag?: string;
  badge?: string;
  url?: string;
  section: BookSection;
  isRecommended?: boolean;
  recommendationOrder?: number;
  version?: number;
  isNew?: boolean;
}

export type BookAdminPayload = Omit<AdminBook, "pk" | "sk" | "version" | "isNew">;
export type CreateBookAdminPayload = Pick<BookAdminPayload, "title"> & Partial<Omit<BookAdminPayload, "title">>;
export interface RegisterBookBarcodePayload {
  sk: string;
  version?: number;
  barcode: string;
}

export interface BookLoanHistory {
  loanId: string;
  bookSk: string;
  title: string;
  barcode: string;
  borrowedAt: string;
  borrowerDong: string;
  borrowerHo: string;
  returnedAt?: string;
  returnedBy?: string;
}

const getBookAdminUrl = () => {
  const adminMainUrl = window.location.hostname === "localhost" ? appConfig.testApiUrl : appConfig.mainApiUrl;
  if (!/\/adminMain\/?$/.test(adminMainUrl)) throw new Error("AdminMain endpoint is not configured");
  return adminMainUrl.replace(/\/adminMain\/?$/, "/adminBook");
};

function getGatewayError(error: unknown) {
  const response = (error as { response?: { status?: number; data?: unknown } })?.response;
  const data = response?.data;
  const errorMessage =
    typeof data === "string"
      ? data
      : data && typeof data === "object"
        ? (data as { error_msg?: unknown; message?: unknown }).error_msg ?? (data as { message?: unknown }).message
        : undefined;

  return {
    code: String(response?.status || "500"),
    response: { error_msg: typeof errorMessage === "string" ? errorMessage : "bookAdminApiError" },
  };
}

/**
 * `/adminBook` is intentionally separate from HttpMainApi so book-admin
 * commands do not alter the existing adminMain command contract.
 */
export class BookAdminApi {
  private async post(command: string, payload: Record<string, unknown> = {}) {
    try {
      const response = await axios.post(
        getBookAdminUrl(),
        { command, ...payload },
        { timeout: 60000 },
      );
      return response.data;
    } catch (error) {
      return getGatewayError(error);
    }
  }

  public get_book_admin_catalog(search: BookAdminSearch) {
    return this.post("get_book_admin_catalog", search);
  }

  public create_book_admin(book: CreateBookAdminPayload) {
    return this.post("create_book_admin", { book });
  }

  public update_book_admin(book: AdminBook) {
    return this.post("update_book_admin", { book });
  }

  public register_book_barcode(book: RegisterBookBarcodePayload) {
    return this.post("register_book_barcode", { book });
  }

  public get_book_loan_history({ status }: { status: BookLoanHistoryStatus }) {
    return this.post("get_book_loan_history", { status });
  }
}

export const bookSectionLabels: Record<BookSection, string> = {
  literature: "소설·에세이",
  humanities_life: "인문·라이프",
  young: "어린이·청소년",
};
